/**
 * Pool de bolas visuales del plinko.
 *
 * EXCEPCIÓN DELIBERADA a la regla "sin Phaser en plinko/logic": este archivo
 * sí importa Phaser/Matter porque su trabajo es, por definición, animar
 * cuerpos físicos. La matemática de cuánto vale cada bola vive en
 * `plinko/logic/*` (puro, testeado); aquí solo se administra el ciclo de
 * vida de hasta `MAX_VISUAL_BALLS` bolas físicas a la vez.
 *
 * El contador lógico del vertido SIEMPRE es la suma de los `weight` de
 * todas las bolas (activas + ya asentadas en el embudo); el pool solo
 * limita cuántos cuerpos Matter coexisten para no reventar el rendimiento
 * en móvil (objetivo: 60 fps con avalancha visual, ver spec sección 7).
 */

import Phaser from 'phaser';
import { TEX } from '../core/TextureFactory';
import type { BarCrossing } from './logic/barMath';
import { MAX_VISUAL_BALLS } from './logic/constants';

/** Radio visual/físico de cada bola, en píxeles. */
export const BALL_RADIUS_PX = 6;

/** Datos lógicos que porta una bola visual mientras cae por el tablero. */
export interface BallData {
  weight: number;
  crossings: BarCrossing[];
  crossedBarIds: Set<string>;
}

interface PooledBall {
  image: Phaser.Physics.Matter.Image;
  data: BallData;
}

/** El body de una `Phaser.Physics.Matter.Image` siempre es un `MatterJS.BodyType` (nunca Arcade). */
function bodyIdOf(image: Phaser.Physics.Matter.Image): number {
  return (image.body as MatterJS.BodyType).id;
}

export class BallPool {
  private active = new Map<number, PooledBall>();
  private free: Phaser.Physics.Matter.Image[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly cap: number = MAX_VISUAL_BALLS
  ) {}

  get activeCount(): number {
    return this.active.size;
  }

  canSpawn(): boolean {
    return this.active.size < this.cap;
  }

  /** Crea (o recicla) una bola visual en `(x, y)` con el peso lógico `weight`. Devuelve su body id. */
  spawn(x: number, y: number, weight: number, vx = 0, vy = 0): number {
    let image = this.free.pop();
    if (!image) {
      image = this.scene.matter.add.image(x, y, TEX.ball);
      image.setCircle(BALL_RADIUS_PX, { friction: 0.02, frictionAir: 0.001, restitution: 0.25 });
    } else {
      image.setPosition(x, y);
      image.setActive(true).setVisible(true);
      image.setStatic(false);
    }
    image.setVelocity(vx, vy);
    image.setAngularVelocity(0);

    const bodyId = bodyIdOf(image);
    this.active.set(bodyId, {
      image,
      data: { weight, crossings: [], crossedBarIds: new Set() },
    });
    return bodyId;
  }

  getData(bodyId: number): BallData | undefined {
    return this.active.get(bodyId)?.data;
  }

  getImage(bodyId: number): Phaser.Physics.Matter.Image | undefined {
    return this.active.get(bodyId)?.image;
  }

  isBall(bodyId: number): boolean {
    return this.active.has(bodyId);
  }

  /** Ids de body de todas las bolas activas ahora mismo (aún no llegaron al embudo). */
  getActiveIds(): number[] {
    return [...this.active.keys()];
  }

  /** Retira una bola del juego activo (llegó al embudo o se descartó) y la recicla. */
  release(bodyId: number): void {
    const entry = this.active.get(bodyId);
    if (!entry) return;
    this.active.delete(bodyId);
    entry.image.setVisible(false);
    entry.image.setStatic(true);
    entry.image.setPosition(-1000, -1000);
    this.free.push(entry.image);
  }

  releaseAll(): void {
    for (const bodyId of [...this.active.keys()]) this.release(bodyId);
  }

  destroy(): void {
    for (const entry of this.active.values()) entry.image.destroy();
    for (const image of this.free) image.destroy();
    this.active.clear();
    this.free = [];
  }
}
